import { BOOL, FLORIDA, MULTI, SINGLE, useConstants } from '../constants';

import type {
    GenericChoice,
    GenericQualificationChoices,
    Qualification,
    QualificationOption,
    ValueType,
} from '../types';

export function useQualificationHelpers(state: string) {
    const {
        DEGREE,
        ECE_DEGREE,
        PLACEHOLDER_HIGH_SCHOOL_CHOICE,
        NO_EDUCATION_REQUIRED_CHOICE,
        CDA,
        YEARS_EXPERIENCE,
        NEW_TO_CHILDCARE,
        YOE_CHOICES_BY_STATE,
    } = useConstants(state);

    return {
        formatQualificationChoices,
    };

    function formatEducationChoices(qualification: Qualification): GenericChoice[] {
        const options = qualification.options.flatMap((option: QualificationOption) => ({
            key: option.id,
            value:
                qualification.name === ECE_DEGREE && option.value !== CDA
                    ? `ECE Related ${option.value}`
                    : option.value,
            order: option.order,
            qualification_type: qualification.value_type,
            pay_scale_qualification: {
                value: null,
                option_id: option.id,
                qualification_id: qualification.id,
                name: qualification.name,
                option_value: option.value,
            },
            qualification,
        }));

        const placeholderChoice =
            state === FLORIDA
                ? { ...NO_EDUCATION_REQUIRED_CHOICE, order: -1, pay_scale_qualification: null }
                : PLACEHOLDER_HIGH_SCHOOL_CHOICE
                  ? { ...PLACEHOLDER_HIGH_SCHOOL_CHOICE, order: -1, pay_scale_qualification: null }
                  : null;

        if (qualification.name === DEGREE) {
            return placeholderChoice ? [...options, placeholderChoice as GenericChoice] : options;
        }

        return options;
    }

    function formatNonBooleanQualifications(
        qualification: Qualification,
        single: boolean,
        addNoneChoice: boolean,
    ): {
        qualification: Qualification;
        choices: GenericChoice[];
    } {
        const choices = qualification.options.map((option: QualificationOption) => ({
            key: option.id,
            value: option.value,
            order: null,
            qualification_type: qualification.value_type,
            pay_scale_qualification: {
                value: null,
                option_id: option.id,
                qualification_id: qualification.id,
                name: qualification.name,
                option_value: option.value ?? null,
            },
            qualification,
        }));

        return single && addNoneChoice
            ? {
                  qualification,
                  choices: [{ key: -1, value: 'None', order: -1, qualification } as GenericChoice, ...choices],
              }
            : { qualification, choices };
    }

    function formatYearsExperienceChoices(qualification: Qualification): GenericChoice[] {
        return YOE_CHOICES_BY_STATE[state].map((choice: { key: number; value: string }, index: number) => ({
            key: choice.key,
            value: choice.value,
            order: index,
            qualification_type: qualification.value_type,
            pay_scale_qualification:
                choice.key === NEW_TO_CHILDCARE.key
                    ? null
                    : {
                          value: choice.key.toString(),
                          option_id: null,
                          qualification_id: qualification.id,
                          name: qualification.name,
                          qualification,
                          option_value: null,
                      },
        }));
    }

    function formatQualificationChoices(qualifications: Qualification[]): GenericQualificationChoices {
        const normal: GenericQualificationChoices = {
            education: [],
            experience: [],
            boolean: [],
            multi: [],
            single: [],
        };

        qualifications.forEach((qualification: Qualification) => {
            switch (qualification.name) {
                case DEGREE:
                case ECE_DEGREE:
                    normal.education = [...normal.education, ...formatEducationChoices(qualification)];
                    break;
                case YEARS_EXPERIENCE:
                    normal.experience = formatYearsExperienceChoices(qualification);
                    break;
                default:
                    switch (qualification.value_type) {
                        case BOOL:
                            normal.boolean = [
                                ...normal.boolean,
                                {
                                    key: qualification.id,
                                    value: qualification.name,
                                    order: null,
                                    qualification_type: BOOL as ValueType,
                                    pay_scale_qualification: {
                                        value: 'true',
                                        option_id: null,
                                        qualification_id: qualification.id,
                                        name: qualification.name,
                                        option_value: null,
                                    },
                                    qualification,
                                },
                            ];
                            break;
                        case SINGLE:
                            normal.single = [
                                ...normal.single,
                                formatNonBooleanQualifications(
                                    qualification,
                                    true,
                                    qualification.web_view_options.include_none_option,
                                ),
                            ];
                            break;
                        case MULTI:
                            normal.multi = [
                                ...normal.multi,
                                formatNonBooleanQualifications(qualification, false, false),
                            ];
                            break;
                        default:
                            break;
                    }
            }
        });

        return normal;
    }
}
